//@flow
import * as React from 'react';
import { Box, Position, Icon, Tooltip } from '@locus-taxy/neo-ui';
import * as Immutable from 'immutable';
import { Editor, EditingMode, DrawPolygonMode } from 'react-map-gl-draw';

import uuidv4 from 'uuid/v4';
import ReactMapGL, {
    Source,
    WebMercatorViewport,
    Layer,
    NavigationControl
} from 'react-map-gl';
import TerritoryMarker from './TerritoryMarker';
import EnvConfig from 'config';
import { TerritoryContext } from './ManualTerritoryProvider';
import NumberUtils from 'common/utils/NumberUtils';

import { CreateTerritory } from 'common/models/internal/CreateTerritoryPlan';

import { neoUIColors } from 'common/enums/ColorEnum';
import TerritoryMapOptions from './TerritoryMapOptions';
import PolygonMapStats from './PolygonMapStats';
import { PolygonOptionTypes } from 'common/entity/pjpMaster/planView/FpAllocatedVisitsContainer';
import { maxBy, minBy } from 'lodash';

const DrawingModes = Immutable.Map({
    editing: {
        label: 'Edit',
        id: 'editing',
        name: 'Editing',
        handler: EditingMode,
        icon: 'edit'
    },
    drawPolygon: {
        label: 'Draw Polygon',
        id: 'drawPolygon',
        name: 'Polygon',
        handler: DrawPolygonMode,
        icon: 'netopt_2d_network'
    },
    deletePolygon: {
        label: 'Remove Polygon',
        id: 'deletePolygon',
        icon: 'delete'
    }
});

const useWindowResize = () => {
    const [windowSize, setWindowSize] = React.useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    React.useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowSize;
};

class DrawingTool extends React.PureComponent {
    onToolClick = () => {
        const { onModeSwitch, mode } = this.props;
        onModeSwitch(mode.id);
    };

    render() {
        const { mode, selectedMode } = this.props;
        return (
            <Tooltip tooltip={mode.label} placement="right">
                <Box
                    bg={mode && mode?.id === selectedMode ? 'blue.200' : null}
                    onClick={this.onToolClick}
                    height="40px"
                    width="40px"
                    sx={{
                        cursor: 'pointer',
                        '&:hover': {
                            bg: 'grey.200'
                        }
                    }}
                    borderBottom="thin solid"
                    borderColor="grey.200"
                    p={3}
                >
                    <Icon size={Icon.Size.medium} icon={mode.icon} />
                </Box>
            </Tooltip>
        );
    }
}

const TerritoryMap = (): React.Node => {
    const {
        selectedTerritoryId,
        setSelectedOutletId,
        selectedTerritoryIndex,
        territories,
        setTerritories,
        unallocatedOutlets,
        selectedOutletId,
        hoveredOutletId,
        hoveredTerritoryId,
        onTerritorySelect,
        onTerritoryHover,
        setHoveredOutletId,
        setUnallocatedOutlets,
        setLayerColorMap,
        layerColorMap,
        onOutletSelect,
        selectedOutletMetrics,
        colorPicker,
        changeTerritory,
        allOutlets,
        clientId,
        planStatus
    } = React.useContext(TerritoryContext);

    const getMinOrMax = React.useCallback((points, minOrMax, latOrLng) => {
        if (minOrMax === 'max') {
            return maxBy(points, value => value[latOrLng])[latOrLng];
        } else {
            return minBy(points, value => value[latOrLng])[latOrLng];
        }
    }, []);

    const getBounds = React.useCallback(
        points => {
            const maxLat = getMinOrMax(points, 'max', 'lat');
            const minLat = getMinOrMax(points, 'min', 'lat');
            const maxLng = getMinOrMax(points, 'max', 'lng');
            const minLng = getMinOrMax(points, 'min', 'lng');

            const southWest = [minLng, minLat];
            const northEast = [maxLng, maxLat];
            return [southWest, northEast];
        },
        [getMinOrMax]
    );

    const getOutletBounds = React.useCallback(() => {
        let allTerritoryPoints = [];
        let unallocatedLatLngs = [];
        if (unallocatedOutlets.size !== 0) {
            unallocatedLatLngs = unallocatedOutlets
                .map(outlet => {
                    const { latLng } = outlet.locationInfo.location;
                    return { lat: latLng.lat, lng: latLng.lng };
                })
                .toJS();
        }
        if (allOutlets.size) {
            territories.forEach(territory => {
                allTerritoryPoints = [
                    ...allTerritoryPoints,
                    ...territory.outerPolygon.points
                ];
            });
        }
        if (
            allTerritoryPoints.length !== 0 ||
            unallocatedLatLngs.length !== 0
        ) {
            return getBounds([...allTerritoryPoints, ...unallocatedLatLngs]);
        }
    }, [allOutlets.size, getBounds, territories, unallocatedOutlets]);

    const { width, height } = useWindowResize();

    const [viewport, setViewport] = React.useState(() => {
        if (territories.size) {
            return {
                width: width - 464,
                height: height - 116
            };
        }
        const bounds = getOutletBounds();
        return new WebMercatorViewport({
            width: width - 464,
            height: height - 116
        }).fitBounds(bounds, { padding: 100 });
    });

    React.useEffect(() => {
        setViewport(
            viewport =>
                new WebMercatorViewport({
                    ...viewport,
                    width,
                    height
                })
        );
    }, [width, height]);

    const [mode, setMode] = React.useState(null);
    const [selectedMode, setSelectedMode] = React.useState(null);

    const [mapControlVisible, setMapControlVisible] = React.useState(false);
    const [polygonStatsVisible, setPolygonStatsVisible] = React.useState(false);

    const [mapOptions, setMapOptions] = React.useState({
        showUnassignedMarkers: true,
        showMarkers: true,
        polygon: true,
        polygonOption: 'all',
        allMarkers: true
    });

    const [mapFeatures, setMapFeatures] = React.useState(null);

    const selectedLayerOutlets = React.useMemo(() => {
        if (selectedTerritoryId) {
            const selectedTerritory = territories.find(territory => {
                return territory.id === selectedTerritoryId;
            });
            return selectedTerritory?.outletsInfo || Immutable.List();
        }
        return Immutable.List();
    }, [selectedTerritoryId, territories]);

    React.useEffect(() => {
        if (selectedTerritoryId) {
            const selectedTerritory = territories.find(territory => {
                return territory.get('id') === selectedTerritoryId;
            });
            const selectedPoints = selectedTerritory.outerPolygon.points;
            if (selectedPoints.length) {
                const bounds = getBounds(selectedPoints);
                setViewport(currentViewPort => {
                    const updatedViewport = new WebMercatorViewport({
                        ...currentViewPort
                    }).fitBounds(bounds, { padding: 100 });
                    return updatedViewport;
                });
            }
        }
    }, [getBounds, selectedTerritoryId]);

    React.useEffect(() => {
        const MapFeatures = territories.map(territory => {
            const outerPolygonPoints = territory.outerPolygon.points;
            const polygonPoints = outerPolygonPoints.map(point => {
                return [point.lng, point.lat];
            });
            const finalPolygonPoints = [];
            finalPolygonPoints.push(polygonPoints);
            return {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'Polygon',
                    coordinates: finalPolygonPoints
                }
            };
        });
        setMapFeatures(MapFeatures.toJS());
    }, [territories]);

    const [selectedFeatureIndex, setSelectedFeatureIndex] = React.useState(
        null
    );
    const editorRef = React.useRef(null);

    const onSelectFeature = React.useCallback(selected => {
        setSelectedFeatureIndex(selected.selectedFeatureIndex);
    }, []);

    const onDelete = React.useCallback(selectedFeatureIndex => {
        if (selectedFeatureIndex !== null && selectedFeatureIndex >= 0) {
            editorRef.current.deleteFeatures(selectedFeatureIndex);
        }
    }, []);

    const onModeSwitch = React.useCallback(
        mode => {
            if (mode === 'deletePolygon') {
                const filteredTerritories = territories.filter(territory => {
                    return territory.id !== selectedTerritoryId;
                });
                const nextTerritoryToSelect =
                    filteredTerritories.first()?.id || null;
                onDelete(selectedFeatureIndex);
                setTerritories(filteredTerritories);
                onTerritorySelect(nextTerritoryToSelect);
                if (selectedLayerOutlets.outlets?.size) {
                    setUnallocatedOutlets(unallocatedOutlets =>
                        Immutable.List(unallocatedOutlets).concat(
                            selectedLayerOutlets.outlets
                        )
                    );
                }
            } else {
                const selectedMode = DrawingModes.get(mode);
                setSelectedMode(DrawingModes.get(mode).id);
                setMode(
                    selectedMode.handler ? new selectedMode.handler() : null
                );
            }
        },
        [
            onDelete,
            onTerritorySelect,
            selectedFeatureIndex,
            selectedLayerOutlets.outlets,
            selectedTerritoryId,
            setTerritories,
            setUnallocatedOutlets,
            territories
        ]
    );

    const territoryOptions = React.useMemo(() => {
        return territories.map(territory => {
            return {
                value: territory.get('id'),
                label: territory.get('name') || territory.get('id')
            };
        });
    }, [territories]);

    const hoveredLayerOutlets = React.useMemo(() => {
        if (hoveredTerritoryId) {
            const selectedTerritory = territories.find(territory => {
                return territory.get('id') === hoveredTerritoryId;
            });
            const territoryOutlets = selectedTerritory.outletsInfo.outlets;
            return territoryOutlets;
        }

        return [];
    }, [territories, hoveredTerritoryId]);

    const getSourceLayer = React.useCallback(() => {
        const SourceLayers = territories.map((territory, index) => {
            const outerPolygonPoints = territory.outerPolygon.points;
            const polygonPoints = outerPolygonPoints.map(point => {
                return [point.lng, point.lat];
            });
            const [initialPoint] = polygonPoints;
            polygonPoints.push(initialPoint);
            const finalPolygonPoints = [];
            const sourceMapFeatures = mapFeatures ? mapFeatures[index] : [];
            finalPolygonPoints.push(polygonPoints);
            const featureJson = {
                type: 'FeatureCollection',
                features: [sourceMapFeatures]
            };
            return featureJson;
        });
        return SourceLayers.toJS();
    }, [mapFeatures, territories]);

    const onMapClick = React.useCallback(
        clickEvent => {
            const { features } = clickEvent;
            if (features.length) {
                const [firstEvent] = features;
                if (firstEvent.source.includes('source-layer')) {
                    const [, , territoryIndex] = firstEvent.source.split('-');
                    const selectedTerritory = territories.get(
                        NumberUtils.toNumber(territoryIndex)
                    );
                    onTerritorySelect(selectedTerritory.get('id'));
                }
            }
        },
        [onTerritorySelect, territories]
    );

    const onMapHover = React.useCallback(
        hoverEvent => {
            const { features } = hoverEvent;
            if (features.length) {
                const [firstEvent] = features;
                if (firstEvent.source.includes('source-layer')) {
                    const [, , territoryIndex] = firstEvent.source.split('-');
                    const selectedTerritory = territories.get(
                        NumberUtils.toNumber(territoryIndex)
                    );
                    onTerritoryHover(selectedTerritory.get('id'));
                } else {
                    onTerritoryHover(null);
                }
            }
        },
        [onTerritoryHover, territories]
    );

    const renderSelectedLayers = () => {
        if (territories.size) {
            const layerColor = layerColorMap.get(selectedTerritoryId);
            const layerOpacity =
                hoveredTerritoryId === selectedTerritoryId ? 0.8 : 0.4;
            if (selectedTerritoryId) {
                return (
                    <Layer
                        visible
                        key={`key-${selectedTerritoryIndex}`}
                        id={`layer-${selectedTerritoryIndex}`}
                        type="fill"
                        source={`source-layer-${selectedTerritoryIndex}`}
                        paint={{
                            'fill-color': layerColor,
                            'fill-opacity': layerOpacity
                        }}
                    />
                );
            }
        }
        return null;
    };

    const renderFilteredLayers = () => {
        const AllLayers = territories.map((territory, index) => {
            const territoryId = territory.get('id');
            const layerColor = layerColorMap.get(territoryId);
            return (
                <Layer
                    visible
                    key={`key-${index}`}
                    id={`layer-${index}`}
                    type="fill"
                    source={`source-layer-${index}`}
                    paint={{
                        'fill-color': layerColor,
                        'fill-opacity': 0.4
                    }}
                />
            );
        });
        return AllLayers;
    };

    const renderAllLayers = () => {
        const AllLayers = territories.map((territory, index) => {
            const territoryId = territory.get('id');
            const layerColor = layerColorMap.get(territoryId);
            const layerOpacity = hoveredTerritoryId === territoryId ? 0.8 : 0.4;
            return (
                <Layer
                    visible
                    key={`key-${index}`}
                    id={`layer-${index}`}
                    type="fill"
                    source={`source-layer-${index}`}
                    paint={{
                        'fill-color': layerColor,
                        'fill-opacity': layerOpacity
                    }}
                />
            );
        });
        return AllLayers;
    };

    const isPointInPolygon = React.useCallback(
        (latitude, longitude, polygon) => {
            if (typeof latitude !== 'number' || typeof longitude !== 'number') {
                throw new TypeError(
                    'Invalid latitude or longitude. Numbers are expected'
                );
            } else if (!polygon || !Array.isArray(polygon)) {
                throw new TypeError(
                    'Invalid polygon. Array with locations expected'
                );
            } else if (polygon.length === 0) {
                throw new TypeError(
                    'Invalid polygon. Non-empty Array expected'
                );
            }

            const x = latitude;
            const y = longitude;

            let inside = false;
            for (
                let i = 0, j = polygon.length - 1;
                i < polygon.length;
                j = i++
            ) {
                const xi = polygon[i][1];
                const yi = polygon[i][0];
                const xj = polygon[j][1];
                const yj = polygon[j][0];

                const intersect =
                    yi > y !== yj > y &&
                    x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
                if (intersect) inside = !inside;
            }

            return inside;
        },
        []
    );

    const onEditorUpdate = React.useCallback(
        feature => {
            if (feature.editType === 'addPosition') {
                const updatedFeature = feature.data[selectedFeatureIndex];
                const updatedFeatures = mapFeatures.map((feature, index) => {
                    if (index === selectedFeatureIndex) {
                        return updatedFeature;
                    }
                    return feature;
                });
                setMapFeatures(updatedFeatures);
            }
            if (feature.editType === 'movePosition') {
                const updatedFeature = feature.data[selectedFeatureIndex];
                const selectedTerritory = territories.get(selectedFeatureIndex);
                const totalOutlets = unallocatedOutlets.concat(
                    selectedTerritory.outletsInfo.outlets
                );
                const [territoryPoints] = updatedFeature?.geometry.coordinates;

                const {
                    territoryOutlets,
                    otherUnallocatedOutlets
                } = totalOutlets.reduce(
                    (allOutlets, outlet) => {
                        const outletPoint = outlet.locationInfo.location.latLng;
                        const isInside = isPointInPolygon(
                            outletPoint.lat,
                            outletPoint.lng,
                            territoryPoints
                        );
                        if (isInside) {
                            allOutlets.territoryOutlets.push(outlet.toJS());
                        } else {
                            allOutlets.otherUnallocatedOutlets = allOutlets.otherUnallocatedOutlets.concat(
                                outlet
                            );
                        }
                        return allOutlets;
                    },
                    {
                        territoryOutlets: [],
                        otherUnallocatedOutlets: Immutable.List()
                    }
                );

                const territoryLatLng = territoryPoints.map(point => {
                    return {
                        lat: point[1],
                        lng: point[0]
                    };
                });
                const updatedFeatures = mapFeatures.map((feature, index) => {
                    if (index === selectedFeatureIndex) {
                        return updatedFeature;
                    }
                    return feature;
                });
                const updatedTerritory = CreateTerritory.fromJS({
                    clientId: selectedTerritory.clientId,
                    code: selectedTerritory.code,
                    id: selectedTerritory.id,
                    name: selectedTerritory.name,
                    outerPolygon: {
                        points: territoryLatLng
                    },
                    outletsInfo: {
                        outletIds: territoryOutlets.map(outlet => ({
                            outletId: outlet.id,
                            clientId: outlet.clientId
                        })),
                        outlets: territoryOutlets
                    }
                });
                const updatedTerritories = territories.set(
                    selectedFeatureIndex,
                    updatedTerritory
                );
                setMapFeatures(updatedFeatures);
                setTerritories(updatedTerritories);
                setUnallocatedOutlets(otherUnallocatedOutlets);
            }
            if (feature.editType === 'addFeature') {
                const latestTerritory = feature.data.pop();
                const [territoryPoints] = latestTerritory.geometry?.coordinates;
                const newTerritoryId = uuidv4();

                const {
                    territoryOutlets,
                    otherUnallocatedOutlets
                } = unallocatedOutlets.reduce(
                    (allOutlets, outlet) => {
                        const outletPoint = outlet.locationInfo.location.latLng;
                        const isInside = isPointInPolygon(
                            outletPoint.lat,
                            outletPoint.lng,
                            territoryPoints
                        );
                        if (isInside) {
                            allOutlets.territoryOutlets.push(outlet.toJS());
                        } else {
                            allOutlets.otherUnallocatedOutlets = allOutlets.otherUnallocatedOutlets.concat(
                                outlet
                            );
                        }
                        return allOutlets;
                    },
                    {
                        territoryOutlets: [],
                        otherUnallocatedOutlets: Immutable.List()
                    }
                );

                const territoryLatLng = territoryPoints.map(point => {
                    return {
                        lat: point[1],
                        lng: point[0]
                    };
                });
                const newTerritory = CreateTerritory.fromJS({
                    clientId,
                    name: `New territory ${territories.size + 1}`,
                    id: newTerritoryId,
                    outerPolygon: {
                        points: territoryLatLng
                    },
                    outletsInfo: {
                        outletIds: territoryOutlets.map(outlet => ({
                            outletId: outlet.id,
                            clientId: outlet.clientId
                        })),
                        outlets: territoryOutlets
                    }
                });
                const updatedLayerColorMap = layerColorMap
                    ? layerColorMap.set(newTerritoryId, colorPicker.pick().code)
                    : Immutable.Map({
                          [newTerritoryId]: colorPicker.pick().code
                      });
                const updatedTerritories = territories.push(newTerritory);
                setLayerColorMap(updatedLayerColorMap);
                setTerritories(updatedTerritories);
                setUnallocatedOutlets(otherUnallocatedOutlets);
                onTerritorySelect(newTerritoryId);
                setMode(null);
                setSelectedMode(null);
            }
        },
        [
            selectedFeatureIndex,
            mapFeatures,
            territories,
            unallocatedOutlets,
            setTerritories,
            setUnallocatedOutlets,
            isPointInPolygon,
            clientId,
            layerColorMap,
            colorPicker,
            setLayerColorMap,
            onTerritorySelect
        ]
    );

    const renderMapDrawToolbox = () => {
        return (
            <Position
                position="absolute"
                zIndex={10}
                top={20}
                left={20}
                bg="white"
            >
                <Box boxShadow="normal">
                    {DrawingModes.toList().map(drawMode => {
                        return (
                            <DrawingTool
                                selectedMode={selectedMode}
                                mode={drawMode}
                                key={drawMode.id}
                                onModeSwitch={onModeSwitch}
                            />
                        );
                    })}
                </Box>
            </Position>
        );
    };

    const selectedTerritory = territories.find(territory => {
        return territory.get('id') === selectedTerritoryId;
    });

    const onMapOptionChange = React.useCallback(options => {
        setMapOptions(options);
    }, []);

    const getMapOptionsRef = React.useRef(null);
    const mapRef = React.useRef(null);
    const getPolygonStatsRef = React.useRef(null);
    const SourceLayers = getSourceLayer();
    const {
        showMarkers,
        polygon,
        showUnassignedMarkers,
        polygonOption,
        allMarkers
    } = mapOptions;
    const visibleOutlets = allMarkers ? allOutlets : selectedLayerOutlets;
    const visiblePolygons =
        polygonOption === PolygonOptionTypes.ALL
            ? renderAllLayers()
            : polygonOption === PolygonOptionTypes.SELECTED
            ? renderSelectedLayers()
            : polygonOption === PolygonOptionTypes.FILTERED
            ? renderFilteredLayers()
            : null;

    return (
        <ReactMapGL
            {...viewport}
            ref={mapRef}
            width="100%"
            height="100%"
            mapboxApiAccessToken={EnvConfig.mapBox.apiKey}
            mapStyle={EnvConfig.mapBox.styleUrl}
            onViewportChange={setViewport}
            onHover={onMapHover}
            onClick={onMapClick}
        >
            {planStatus !== 'FROZEN' ? (
                <Editor
                    // to make the lines/vertices easier to interact with
                    ref={editorRef}
                    clickRadius={12}
                    mode={mode}
                    onSelect={onSelectFeature}
                    onUpdate={onEditorUpdate}
                    selectedFeatureIndex={selectedFeatureIndex}
                    editHandleShape={'circle'}
                    features={mapFeatures}
                />
            ) : null}
            <TerritoryMapOptions
                ref={getMapOptionsRef}
                mapControlVisible={mapControlVisible}
                setMapControlVisible={setMapControlVisible}
                mapOptions={mapOptions}
                {...mapOptions}
                unallocatedCount={unallocatedOutlets.length}
                onChange={onMapOptionChange}
            />
            <PolygonMapStats
                selectedOutletMetrics={selectedOutletMetrics}
                hoveredLayerOutlets={hoveredLayerOutlets}
                ref={getPolygonStatsRef}
                mapControlVisible={mapControlVisible}
                color={
                    layerColorMap && layerColorMap.size !== 0
                        ? layerColorMap.get(
                              selectedTerritoryId || hoveredTerritoryId
                          )
                        : null
                }
                polygonStatsVisible={polygonStatsVisible}
                setPolygonStatsVisible={setPolygonStatsVisible}
            />

            {SourceLayers.map((layer, index) => {
                return (
                    <Source
                        key={`source-layer-${index}`}
                        id={`source-layer-${index}`}
                        type="geojson"
                        data={layer}
                    />
                );
            })}
            {polygon ? visiblePolygons : null}
            {planStatus !== 'FROZEN' ? renderMapDrawToolbox() : null}
            {showMarkers && selectedTerritoryId && !allMarkers
                ? visibleOutlets.outlets.map(outlet => {
                      return (
                          <TerritoryMarker
                              territoryName={selectedTerritory.name}
                              planStatus={planStatus}
                              changeTerritory={changeTerritory}
                              territoryOptions={territoryOptions}
                              setSelectedOutletId={setSelectedOutletId}
                              onOutletSelect={onOutletSelect}
                              key={outlet.id}
                              id={outlet.id}
                              outlet={outlet}
                              territoryId={selectedTerritoryId}
                              color={layerColorMap.get(selectedTerritoryId)}
                              isSelected={
                                  outlet.id === selectedOutletId ||
                                  outlet.id === hoveredOutletId
                              }
                              selectedTerritory={selectedTerritory}
                              setHoveredOutletId={setHoveredOutletId}
                          />
                      );
                  })
                : null}
            {showMarkers && allMarkers
                ? territories.map(territory => {
                      const { outlets } = territory.outletsInfo;
                      return outlets.map(outlet => {
                          return (
                              <TerritoryMarker
                                  territoryName={territory.name}
                                  planStatus={planStatus}
                                  changeTerritory={changeTerritory}
                                  territoryOptions={territoryOptions}
                                  setSelectedOutletId={setSelectedOutletId}
                                  onOutletSelect={onOutletSelect}
                                  territoryId={selectedTerritoryId}
                                  key={outlet.id}
                                  id={outlet.id}
                                  outlet={outlet}
                                  color={layerColorMap.get(territory.get('id'))}
                                  isSelected={
                                      outlet.id === selectedOutletId ||
                                      outlet.id === hoveredOutletId
                                  }
                                  selectedTerritory={selectedTerritory}
                                  setHoveredOutletId={setHoveredOutletId}
                              />
                          );
                      });
                  })
                : null}
            {showUnassignedMarkers &&
                unallocatedOutlets.map(outlet => {
                    return (
                        <TerritoryMarker
                            planStatus={planStatus}
                            changeTerritory={changeTerritory}
                            territoryOptions={territoryOptions}
                            setSelectedOutletId={setSelectedOutletId}
                            key={outlet.id}
                            id={outlet.id}
                            outlet={outlet}
                            selectedTerritory={selectedTerritory}
                            color={neoUIColors.red500.code}
                            isSelected={
                                outlet.id === selectedOutletId ||
                                outlet.id === hoveredOutletId
                            }
                            isUnallocated
                            setHoveredOutletId={setHoveredOutletId}
                        />
                    );
                })}
            <NavigationControl
                style={{
                    right: 10,
                    bottom: 40
                }}
            />
        </ReactMapGL>
    );
};

export default TerritoryMap;
